import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, AreaChart, Area, BarChart, Bar, CartesianGrid } from "recharts";
import { Calculator, Home, TrendingUp, ArrowRight, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function MortgageCalc() {
  const [price, setPrice] = useState(1200000);
  const [down, setDown] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(30);

  const { monthly, schedule } = useMemo(() => {
    const loan = price * (1 - down / 100);
    const r = rate / 100 / 12;
    const n = years * 12;
    const m = loan * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const schedule = [];
    let bal = loan;
    for (let y = 1; y <= Math.min(years, 30); y += 5) {
      let temp = loan;
      for (let i = 0; i < y * 12; i++) {
        const interest = temp * r;
        temp = temp - (m - interest);
      }
      schedule.push({ year: `Y${y}`, balance: Math.max(0, Math.round(temp)), paid: Math.round(loan - Math.max(0, temp)) });
    }
    return { monthly: m, schedule };
  }, [price, down, rate, years]);

  return (
    <div className="glass rounded-3xl p-7 sm:p-8" data-testid="mortgage-calculator">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl bg-navy-900 text-gold-400 flex items-center justify-center">
          <Calculator size={18} />
        </div>
        <div>
          <div className="font-heading text-xl text-navy-900">Mortgage Calculator</div>
          <div className="text-xs text-navy-600">Estimate your monthly payment</div>
        </div>
      </div>

      <div className="space-y-4">
        <Field label="Home price" value={`$${price.toLocaleString()}`}>
          <input type="range" min="200000" max="5000000" step="50000" value={price} onChange={(e) => setPrice(+e.target.value)} data-testid="calc-price" className="w-full accent-gold-500" />
        </Field>
        <Field label="Down payment" value={`${down}%`}>
          <input type="range" min="5" max="60" step="1" value={down} onChange={(e) => setDown(+e.target.value)} data-testid="calc-down" className="w-full accent-gold-500" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Interest rate" value={`${rate.toFixed(2)}%`}>
            <input type="range" min="2" max="10" step="0.1" value={rate} onChange={(e) => setRate(+e.target.value)} data-testid="calc-rate" className="w-full accent-gold-500" />
          </Field>
          <Field label="Term" value={`${years} yr`}>
            <input type="range" min="10" max="30" step="5" value={years} onChange={(e) => setYears(+e.target.value)} data-testid="calc-years" className="w-full accent-gold-500" />
          </Field>
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-navy-900 p-5 text-white">
        <div className="text-xs uppercase tracking-[0.22em] text-gold-400 font-semibold">Estimated Monthly</div>
        <div className="mt-1 font-heading text-4xl text-white" data-testid="calc-monthly">
          ${Math.round(monthly).toLocaleString()}
          <span className="text-base text-white/60 font-body"> /mo</span>
        </div>
        <div className="mt-4 h-24">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={schedule}>
              <defs>
                <linearGradient id="bal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.6} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="balance" stroke="#D4AF37" strokeWidth={2} fill="url(#bal)" />
              <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #D4AF37", borderRadius: 8, color: "#fff" }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-navy-700">{label}</span>
        <span className="text-xs font-semibold text-navy-900">{value}</span>
      </div>
      {children}
    </div>
  );
}

function HomeValueCard() {
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const sample = [
    { m: "Jan", v: 1.15 },
    { m: "Mar", v: 1.18 },
    { m: "May", v: 1.21 },
    { m: "Jul", v: 1.25 },
    { m: "Sep", v: 1.29 },
    { m: "Nov", v: 1.33 },
  ];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/home-value`, { address, email: email || undefined });
      setResult(data);
      toast.success("Estimate ready", { description: "Scroll down — your range is below." });
    } catch (err) {
      toast.error("Could not estimate", { description: "Please try a different address." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass rounded-3xl p-7 sm:p-8" data-testid="home-value-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl bg-gold-500 text-navy-900 flex items-center justify-center">
          <Home size={18} />
        </div>
        <div>
          <div className="font-heading text-xl text-navy-900">What's My Home Worth?</div>
          <div className="text-xs text-navy-600">Probate PCMA — within minutes</div>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3" data-testid="home-value-form">
        <input
          required
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          data-testid="hv-address"
          placeholder="Property address"
          className="w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="hv-email"
          placeholder="Email (optional)"
          className="w-full rounded-xl border border-navy-200 bg-white/80 px-4 py-3 text-sm focus:outline-none focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20"
        />
        <button data-testid="hv-submit" disabled={loading} className="w-full btn-primary group disabled:opacity-60">
          {loading ? "Calculating..." : "Get My Estimate"}
          <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
        </button>
      </form>

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-5 rounded-2xl border border-gold-500/40 bg-gold-500/5 p-5"
          data-testid="hv-result"
        >
          <div className="text-xs uppercase tracking-[0.22em] text-gold-700 font-semibold">Estimated Range</div>
          <div className="mt-1 font-heading text-2xl text-navy-900">
            ${result.range_low.toLocaleString()} – ${result.range_high.toLocaleString()}
          </div>
          <div className="text-xs text-navy-600 mt-1">{result.confidence}</div>
        </motion.div>
      )}

      <div className="mt-6 h-32">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={sample}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="m" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}M`} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #D4AF37", borderRadius: 8 }} />
            <Line type="monotone" dataKey="v" stroke="#0F172A" strokeWidth={2.5} dot={{ fill: "#D4AF37", r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 text-[10px] text-navy-500 uppercase tracking-[0.2em] font-semibold">OC Market — 12 mo trend</div>
    </div>
  );
}

function ClientTestimonialCard() {
  const breakdown = [
    { label: "5★", v: 187 },
    { label: "4★", v: 24 },
    { label: "3★", v: 4 },
    { label: "2★", v: 1 },
    { label: "1★", v: 0 },
  ];
  return (
    <div className="glass rounded-3xl p-7 sm:p-8" data-testid="client-testimonials-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-xl bg-navy-800 text-gold-400 flex items-center justify-center">
          <MessageCircle size={18} />
        </div>
        <div>
          <div className="font-heading text-xl text-navy-900">Client Testimonials</div>
          <div className="text-xs text-navy-600">216 verified reviews</div>
        </div>
      </div>

      <div className="flex items-baseline gap-3">
        <div className="font-heading text-5xl text-navy-900">4.9</div>
        <div className="text-sm text-navy-600">/ 5.0 average</div>
      </div>
      <div className="mt-1 flex items-center gap-1">
        {[0,1,2,3,4].map((s) => (
          <TrendingUp key={s} size={14} className="text-gold-500" />
        ))}
        <span className="ml-2 text-xs text-navy-600">Trending up 12% YoY</span>
      </div>

      <div className="mt-5 h-40">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={breakdown} layout="vertical">
            <XAxis type="number" hide />
            <YAxis dataKey="label" type="category" stroke="#475569" fontSize={11} tickLine={false} axisLine={false} width={28} />
            <Bar dataKey="v" fill="#D4AF37" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <a href="#testimonials" data-testid="client-testimonials-read" className="mt-4 inline-flex items-center text-sm font-semibold text-navy-800 hover:text-gold-600 transition-colors group">
        Read all reviews
        <ArrowRight size={14} className="ml-1.5 transition-transform group-hover:translate-x-1" />
      </a>
    </div>
  );
}

export default function HomeValueMortgage() {
  return (
    <section id="home-value" data-testid="home-value-section" className="py-24 sm:py-32 bg-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="section-overline">Numbers, Made Beautiful</span>
          <h2 className="mt-3 font-heading text-4xl sm:text-5xl text-navy-900 leading-tight tracking-tight">
            Run the numbers — <em className="italic text-gold-600">together</em>.
          </h2>
        </div>

        <div id="mortgage" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <MortgageCalc />
          <HomeValueCard />
          <ClientTestimonialCard />
        </div>
      </div>
    </section>
  );
}
